import { queryOptions } from "@tanstack/react-query";
import type { ProjectService } from "@/services/project.service";
import { FRESHNESS, withinAttempt } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The project reads the caching pilot moves onto Query (NG-2543, contract §1):
 *
 * - **R1** `project.detail`: one project, `[slug, uid, 'project', id]`. Mutable,
 *   because it is working data a screen edits and another tab may change.
 * - **R2** `project.bank`: every project in the workspace, walked across all
 *   pages, `[slug, uid, 'projects', 'bank', {archived}]`. Reference data: the
 *   pickers and filters that read it need names and ids, which rarely change.
 *
 * R1 sits under `'project'` so a prefix invalidation of `('project', id)` also
 * reaches the sub-resources (R3 onwards) keyed beneath it; R2 sits under
 * `'projects'` so it does not. Both are plain idempotent GETs, so both declare
 * `"safe-read"` and `runRead` owns their retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a loader passes `getRootStore().projectService` and a test passes a fake.
 */

/** The R1 key. */
export function projectDetailKey(scope: QueryScope, projectId: string) {
  return scopedKey(scope, "project", projectId);
}

/**
 * The R2 key. `archived` is part of the representation: the backend lists
 * active projects unless asked, and the two lists are separate entries.
 */
export function projectBankKey(
  scope: QueryScope,
  { archived = false }: { archived?: boolean } = {},
) {
  return scopedKey(scope, "projects", "bank", { archived });
}

/** R1: one project's detail, in `scope`'s workspace. */
export function projectDetailQuery(
  service: Pick<ProjectService, "getProject">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectDetailKey(scope, projectId),
    queryFn: scopedRead((request) => service.getProject(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * R2: the workspace's whole project list, every page. `archived: false` sends
 * no parameter, the same request `ProjectStore.fetchProjects` makes today.
 *
 * A retry restarts the walk from the first page. `runRead` sizes one timeout
 * per attempt, and the walk makes a request per page, so the whole walk is
 * bounded by it rather than each page: past it the walk aborts, which `runRead`
 * treats as final, and the skeleton never outlives the interactive deadline.
 */
export function projectBankQuery(
  service: Pick<ProjectService, "fetchAll">,
  scope: QueryScope,
  { archived = false }: { archived?: boolean } = {},
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: projectBankKey(scope, { archived }),
    queryFn: scopedRead(
      (request) =>
        withinAttempt(request, (bounded) =>
          service.fetchAll(scope.tenant, archived ? { archived: true } : {}, bounded),
        ),
      { retry: "safe-read" },
    ),
  });
}
